import { addDays } from 'date-fns'
import { defineStore } from 'pinia'
import Visitor from '@/shared/models/Visitor'
import { useCacheService } from '@/shared/composables/useCacheService'
import { DEFAULT_DATE_GAP } from '@/shared/constants/dates.constants'
import policyMock from '@mocks/policy'
import { VISA } from '@/shared/composables/useEntry'
import type {
  BackendVisitorType,
  VisitorsStateQuote,
  VisitorType,
} from '@/shared/types/store.types'
import { GetPolicyResponse, GetQuoteResponse } from '@/shared/types/response.types'
import { capitalizeFirstLetter, formatDate, newVisitor, parseDate } from '@/shared/utils'
import { toPolicyTimestamp } from '@qicgroupdoha/travel-devkit/helpers/dates'
import { useCoreStore } from './store'
import { useIsHolder } from '@/shared/composables/useIsHolder'
import { DEFAULT_NUMBER_OF_VISITORS, QATAR_INBOUND_KEY } from '@/shared/constants/other.constants'
import { useApiNew } from '@/shared/composables/useApi'
import { SendFormDataFields, SendFormDataResult } from '@/shared/types/api.types'
import { CountryWithDetailsType } from '../types/other.types'

const localStorageCacheService = useCacheService()
const sessionStorageCacheService = useCacheService('session')
const apiService = useApiNew()
const decodeValue = (str: string) =>
  str.replace(/&#(\d+);/g, (match, dec) => String.fromCharCode(dec))

const parseBackendDate = (date?: string): Date | null =>
  date ? parseDate(date, 'dd/MM/yyyy') : null

const parseBackendPolicyDuration = (duration?: string): string | undefined => {
  const coreStore = useCoreStore()
  coreStore.getDurations()
  const durationsList = coreStore.durations

  if (!duration || !durationsList) return undefined

  const durationString = String(duration)

  if (durationString.includes('V')) {
    return durationString
  }

  let durationNumber = Number(duration)

  if (durationNumber > 330) {
    durationNumber = 365
  } else {
    durationNumber = Math.ceil(durationNumber / 30) * 30
  }

  return durationsList.find((el) => el.duration === durationNumber)?.code
}

const travellerToVisitor = (visitor: BackendVisitorType, holder: Nullable<VisitorType> = null) => {
  const [bDay, bMonth, bYear] = visitor.dateOfBirth.split('/')
  const birthDate = new Date(bYear, bMonth - 1, bDay)

  const [pDay, pMonth, pYear] = visitor.passportExpiryDate.split('/')
  const passportExpiryDate = new Date(pYear, pMonth - 1, pDay)
  const phone = `+${visitor.phone.trim()}`
  let contactsSame = true

  if (holder) {
    contactsSame = visitor.email === holder.email && phone === holder.phoneInfo.phone
  }

  return newVisitor({
    firstName: decodeValue(visitor.firstName),
    lastName: decodeValue(visitor.lastName),
    birthDate,
    nationality: visitor.nationalityCode,
    nationalityName: visitor.nationalityName,
    country: visitor.residenceCountry,
    passport: visitor.passportNo,
    visa: visitor.visaNo,
    getVisa: !visitor.visaNo,
    contactsSame,
    email: visitor.email,
    phoneInfo: {
      phone,
      countryCode: visitor.phoneCountry,
    },
    id: '',
    srNo: visitor.srNo,
    relationship: visitor.relationCode,
    relationshipName: visitor.relationName,
    gender: visitor.gender,
    passportExpiryDate,
    visaValidityDate: parseBackendDate(visitor.visaValidityDate) || undefined,
    policyDuration: parseBackendPolicyDuration(visitor.policyDuration),
    durationMatch: visitor.durationMatch,
    maritalStatus: visitor.maritalStatus || '',
  })
}

export const STATE_CACHE_KEY = 'visitors-state'

const getCacheState = () => ({
  ...localStorageCacheService.getFromStorage(STATE_CACHE_KEY),
  ...sessionStorageCacheService.getFromStorage(STATE_CACHE_KEY),
})

type VisitorPreapprovalType = Omit<
  VisitorType,
  | 'email'
  | 'phoneInfo'
  | 'id'
  | 'srNo'
  | 'relationship'
  | 'relationshipName'
  | 'passportExpiryDate'
  | 'country'
>

interface VisitorsState {
  holder: VisitorType
  visitors: VisitorType[]
  editVisitorId: Nullable<number>
  visitDate: Date
  entryType: string
  entryValue: string
  quote: VisitorsStateQuote
  numberOfVisitors: number
  isInboundAddon: boolean
  inboundPrice?: number
  inboundQuoteNumber?: string
  inboundProductCode?: string
  inboundSchemeCode?: string
  lastHolderNationality?: Nullable<string>
  lastHolderNationalityName?: Nullable<string>
  manualSelectedInboundAddon?: Nullable<boolean>
  partnerCampaign?: Nullable<string>
  assuredCode?: Nullable<string>
}

const DEFAULT_VISITOR_DATA = {
  srNo: 0,
  firstName: '',
  lastName: '',
  birthDate: null,
  nationality: null,
  nationalityName: null,
  country: null,
  passport: '',
  visa: '',
  email: '',
  phoneInfo: {
    phone: '+974',
    countryCode: 'QA',
    isValid: false,
  },
  id: '',
  getVisa: true,
  contactsSame: true,
  relationship: '',
  relationshipName: '',
  gender: 1,
  passportExpiryDate: null,
  numberOfVisitors: DEFAULT_NUMBER_OF_VISITORS,
  maritalStatus: '',
}

const restoreHolder = (
  cachedHolder: Partial<VisitorType>,
  countryOfResidence: string | null
): VisitorType => {
  const holder = {
    ...DEFAULT_VISITOR_DATA,
    ...cachedHolder,
    country: countryOfResidence || cachedHolder?.country || DEFAULT_VISITOR_DATA.country,
  }

  if (cachedHolder?.birthDate) {
    holder.birthDate = new Date(cachedHolder.birthDate)
  }
  if (cachedHolder?.passportExpiryDate) {
    holder.passportExpiryDate = new Date(cachedHolder.passportExpiryDate)
  }
  if (cachedHolder?.visaValidityDate) {
    holder.visaValidityDate = new Date(cachedHolder.visaValidityDate)
  }

  holder.passportImage = null

  return holder
}

const checkOtherVisitors = (cacheState: Partial<VisitorsState>) => {
  if (cacheState.visitors && cacheState.visitors.length > 0) {
    for (const visitor of cacheState.visitors) {
      if (visitor.birthDate) {
        visitor.birthDate = new Date(visitor.birthDate)
      }
      if (visitor.passportImage) {
        visitor.passportImage = null
      }
    }
  } else {
    cacheState.visitors = []
  }
}

const useDefaultState = (): VisitorsState => {
  const cacheState = getCacheState()

  return {
    holder: newVisitor(DEFAULT_VISITOR_DATA),
    visitors: [],
    editVisitorId: null,
    visitDate: cacheState?.visitDate ? new Date(cacheState.visitDate) : new Date(),
    entryType: VISA,
    entryValue: cacheState?.entryValue || '',
    quote: {
      quote: '',
      schemes: [],
    },
    numberOfVisitors: cacheState?.numberOfVisitors || DEFAULT_NUMBER_OF_VISITORS,
    isInboundAddon: cacheState?.isInboundAddon || false,
    lastHolderNationality: cacheState?.lastHolderNationality || null,
    lastHolderNationalityName: cacheState?.lastHolderNationalityName || null,
    manualSelectedInboundAddon: cacheState?.manualSelectedInboundAddon ?? null,
    inboundPrice: cacheState?.inboundPrice || undefined,
    partnerCampaign: cacheState?.partnerCampaign || null,
    assuredCode: cacheState?.assuredCode || null,
  }
}

export const useVisitorsStore = defineStore('visitors', {
  state: (): VisitorsState => {
    const cacheState = getCacheState()
    const countryOfResidence = localStorage.getItem(QATAR_INBOUND_KEY)

    if (cacheState && cacheState?.holder) {
      cacheState.visitDate = cacheState?.visitDate ? new Date(cacheState.visitDate) : new Date()
      cacheState.entryType = VISA
      cacheState.entryValue ||= ''
      cacheState.quote ||= { quote: '', schemes: [] }
      cacheState.lastHolderNationality ||= null
      cacheState.lastHolderNationalityName ||= null
      cacheState.manualSelectedInboundAddon ??= null
      cacheState.inboundPrice ||= undefined
      cacheState.partnerCampaign ||= null
      cacheState.assuredCode ||= null
      cacheState.holder = restoreHolder(cacheState.holder, countryOfResidence)

      checkOtherVisitors(cacheState)

      return cacheState
    }
    return useDefaultState()
  },

  getters: {
    visitorsList(state): VisitorType[] {
      return [state.holder, ...state.visitors]
    },
    visitorsMap(): Map<number, VisitorType> {
      return new Map(this.visitorsList.map((visitor: VisitorType) => [visitor.srNo, visitor]))
    },
    getQuoteData: (state): VisitorsStateQuote => state.quote,
    getQuotePrice: (state): Nullable<number> =>
      parseInt(state.quote.schemes[0]?.policyPrice) ?? null,
    getEntryValue: (state): string => state.entryValue,
    getVisitDates: (state): { startDate: Date; tillDate: Date } => ({
      startDate: state.visitDate,
      tillDate: addDays(state.visitDate, DEFAULT_DATE_GAP),
    }),
    getAnalyticsEntryType(state) {
      return state.entryType === 'visa'
    },
  },

  actions: {
    addVisitor(visitorDTO: VisitorType) {
      visitorDTO.srNo = 0
      this.visitors.push(newVisitor(visitorDTO))
    },

    editVisitor(visitorDTO: VisitorType) {
      const visitor = this.visitors.find((visitor: VisitorType) => visitor.srNo === visitorDTO.srNo)
      Object.assign({ visitor }, visitorDTO)
    },

    removeVisitor(visitorObj: VisitorType) {
      this.visitors = this.visitors.filter((visitor: VisitorType) => visitor.id !== visitorObj.id)
    },

    removeVisitors() {
      this.holder = newVisitor(DEFAULT_VISITOR_DATA)
      this.visitors = []
    },

    setEditVisitorId(visitorId: number) {
      this.editVisitorId = visitorId
    },

    setVisitDate(visitDate: Date) {
      this.visitDate = visitDate
    },

    setEntryValue(value: string) {
      this.entryValue = value
    },

    setCampaignParams({
      partnerCampaign,
      assuredCode,
    }: {
      partnerCampaign?: string
      assuredCode?: string
    }) {
      if (partnerCampaign) this.partnerCampaign = partnerCampaign
      if (assuredCode) this.assuredCode = assuredCode
    },

    clearState() {
      localStorageCacheService.delete(STATE_CACHE_KEY)
      sessionStorageCacheService.delete(STATE_CACHE_KEY)
      this.$reset()
    },

    setTravellers(visitors: BackendVisitorType[]) {
      const holder = visitors.find((visitor: BackendVisitorType) =>
        useIsHolder(visitor.relationCode)
      )
      if (holder) {
        this.holder = travellerToVisitor(holder)
        this.holder.phoneInfo.isValid = true
      }
      const persons = visitors.filter((visitor) => !useIsHolder(visitor.relationCode))
      this.visitors = persons.map((person) => {
        const visitor = travellerToVisitor(person, this.holder)
        visitor.phoneInfo.isValid = true
        return visitor
      })
    },

    removeTraveller(visitor: VisitorType) {
      this.removeVisitor(visitor)

      return apiService.travellerService?.removeTraveller({
        srNo: visitor.srNo,
      })
    },

    async removeInsuredTravellers() {
      const visitDate = this.visitDate
      await apiService.travellerService?.removeInsuredTravellers()
      this.clearState()
      this.setVisitDate(visitDate)
      await this.getTravellers()

      const { holder, visitors, ...visitorsInfo } = this.$state
      localStorageCacheService.set(STATE_CACHE_KEY, visitorsInfo)
      sessionStorageCacheService.set(STATE_CACHE_KEY, { holder, visitors })
    },

    removeTravellers(hasUninsuredUsers = false) {
      if (hasUninsuredUsers) return this.removeInsuredTravellers()

      apiService.travellerService?.removeTravellers(
        this.visitorsList.map((visitor) => visitor.srNo)
      )
      this.removeVisitors()
      this.clearState()

      return undefined
    },

    sendFormDataStepped(payload: SendFormDataFields) {
      return apiService.commonSeervice?.sendFormData(payload)
    },

    async getQuote(visitorsCount: Nullable<number> = null) {
      let res: SendFormDataResult
      if (visitorsCount) {
        res = await apiService.commonSeervice?.sendFormData({
          numadults: visitorsCount,
          policyDate: toPolicyTimestamp(this.visitDate),
        })
      }

      return apiService.quoteService
        ?.getQuote(this.assuredCode || undefined)
        .then((quoteResponse: GetQuoteResponse) => {
          this.quote = {
            quote: quoteResponse.quoteno,
            schemes: quoteResponse.schemes,
          }
          return res
        })
    },

    updateVisitor(visitorDTO: Visitor) {
      let isHolder = false
      if (visitorDTO.srNo === 0) {
        this.addVisitor(visitorDTO)
      } else if (useIsHolder(visitorDTO.relationship)) {
        isHolder = true
        Object.assign(this.holder, visitorDTO)
      } else {
        this.editVisitor(visitorDTO)
      }

      const tasks = []

      tasks.push(apiService.travellerService?.saveTraveller(visitorDTO.toRequest()))

      if (isHolder) {
        this.visitors.forEach((visitor) => {
          let visitorToUpdate = { ...visitor }

          if (!visitorToUpdate.visaValidityDate) {
            visitorToUpdate.policyDuration = this.entryValue
          }

          if (visitorToUpdate.contactsSame) {
            visitorToUpdate = {
              ...visitorToUpdate,
              email: visitorDTO.email,
              phoneInfo: {
                phone: visitorDTO.phoneInfo.phone,
                countryCode: visitorDTO.phoneInfo.countryCode,
              },
            }
          }

          if (JSON.stringify(visitor) !== JSON.stringify(visitorToUpdate)) {
            const linkedVisitor = new Visitor(visitorToUpdate, String(visitorToUpdate.id))
            this.editVisitor(linkedVisitor)
            tasks.push(apiService.travellerService?.saveTraveller(linkedVisitor.toRequest()))
          }
        })
      }

      return Promise.all(tasks)
    },

    getTravellers() {
      return apiService.travellerService?.getTravellers().then((res) => {
        const list = res?.sort(
          (prev: BackendVisitorType, current: BackendVisitorType) => prev.srNo - current.srNo
        )
        this.setTravellers(list)
      })
    },

    updateQuote(mode: 'visitors' | 'addon' = 'visitors') {
      if (mode === 'visitors') {
        if (this.quote.quote !== undefined) {
          return apiService.quoteService?.updateVisitorsQuote({ quoteno: this.quote.quote })
        }
      } else if (
        mode === 'addon' &&
        this.inboundQuoteNumber &&
        this.inboundProductCode &&
        this.inboundSchemeCode &&
        this.quote.quote
      ) {
        return apiService.quoteService?.updateVisitorsQuote({
          quoteno: this.quote.quote,
          inboundquoteno: this.inboundQuoteNumber,
          inboundproductcode: this.inboundProductCode,
          inboundschemecode: this.inboundSchemeCode,
        })
      }

      return Promise.reject(undefined)
    },

    getQuoteDetails(): Promise<VisitorType[]> {
      return apiService.quoteService
        ?.getQuoteDetails({
          quoteno: this.quote.quote,
          phone: this.holder.phoneInfo.phone,
        })
        .then((res) => {
          if (!res.visitors) {
            return this.visitors
          }

          const matchingVisitorNames = res.visitors.map(
            (visitor) => `${visitor.firstName} ${visitor.lastName}`
          )
          return this.visitorsList.filter((visitor) =>
            matchingVisitorNames.includes(`${visitor.firstName} ${visitor.lastName}`)
          )
        })
        .catch((error) => {
          console.error('Error getting quote details: ', error)
          return Promise.reject(undefined)
        })
    },

    async getPolicy(): Promise<GetPolicyResponse | typeof policyMock> {
      if (import.meta.env.VUE_APP_ENV !== 'PRODUCTION') {
        return new Promise((resolve) => {
          resolve(policyMock)
        })
      }
      const { data } = await apiService.policyService.getPolicy()
      return data
    },

    getQuotePayments(quoteno: string) {
      return apiService.quoteService?.getQuotePayments(quoteno)
    },

    getPreapprovalVisa(visitor: VisitorType): Promise<VisitorPreapprovalType> {
      return apiService.travellerService
        ?.getPreapprovalVisa(visitor.passport, visitor.nationality!)
        .then((response) => {
          const data = response
          const nameArray = data.nameEn.split(' ')
          const firstName = nameArray.shift() || ''
          const lastName = nameArray.join(' ')

          const policyDuration = parseBackendPolicyDuration(data.coverageDuration)
          if (policyDuration && useIsHolder(visitor.relationship)) {
            this.setEntryValue(policyDuration)
          }

          return {
            firstName,
            lastName,
            birthDate: parseBackendDate(data.dob),
            nationality: data.nationalityCode,
            nationalityName: capitalizeFirstLetter(data.nationality),
            passport: data.passportNo,
            visa: data.visaNo,
            getVisa: false,
            contactsSame: false,
            gender: Number(data.gender),
            visaValidityDate: parseBackendDate(data.visaValidityDate) || undefined,
            policyDuration,
          }
        })
        .catch((error) => {
          console.error('Error getting preapproval visa', error)
          return {
            ...visitor,
            visaValidityDate: undefined,
            policyDuration: this.entryValue,
          }
        })
    },

    setNumberOfVisitors(numberOfVisitors: number) {
      this.numberOfVisitors = numberOfVisitors
    },

    setInboundAddon(isInboundAddon: boolean) {
      this.isInboundAddon = isInboundAddon
    },

    async inboundAddonCheck(civilId: string, startDate: string | Date, endDate: string | Date) {
      const formattedVisitDates = (date: Date | string) =>
        formatDate(new Date(date), 'en', 'yyyy-MM-dd')
      return await apiService.travellerService?.inboundAddonCheck(
        civilId,
        formattedVisitDates(startDate),
        formattedVisitDates(endDate)
      )
    },

    async getInboundAddonQuote() {
      const res = await apiService.quoteService.getTravelQuote().then((res) => {
        const sheme = res.schemes[0]
        this.inboundQuoteNumber = res.quoteno
        this.inboundProductCode = sheme.productcode
        this.inboundSchemeCode = sheme.schemecode
        this.inboundPrice = sheme.price
      })

      return res
    },

    updateVisitorPhoneInfo(
      srNo: number,
      phoneInfo: { phone: string; countryCode: string; isValid: boolean }
    ) {
      const visitor = this.visitorsList.find((visitor) => visitor.srNo === srNo)
      if (visitor) {
        visitor.phoneInfo = phoneInfo
      }
    },

    setHolderNationality(nationality: CountryWithDetailsType) {
      this.holder.nationality = nationality.code
      this.holder.nationalityName = nationality.name
      this.lastHolderNationality = nationality.code
      this.lastHolderNationalityName = nationality.name
    },

    setManualSelectedInboundAddon(manualSelectedInboundAddon: boolean) {
      this.manualSelectedInboundAddon = manualSelectedInboundAddon
    },
  },
})
